import {Component, OnDestroy, OnInit} from '@angular/core';
import {take, takeUntil} from 'rxjs/operators';
import {SpreadSheetEvent, SpreadSheetEventModel} from '@features/spreadsheet/models/spread-sheet';
import {FlexSheetService} from '@features/spreadsheet/services/flex-sheet.service';
import {Subject} from 'rxjs';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {ISideNoteComponent} from '@features/spreadsheet/models/footnote';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {Api} from '@shared/constants/common-field.constants';
import {getCbcFormsSelector} from '@features/spreadsheet/store/spreadsheet.selector';
import {select, Store} from '@ngrx/store';
import {SpreadsheetState} from '@features/spreadsheet/store/spreadsheet.reducer';
import {GetCbcFormsAction} from '@features/spreadsheet/store/spreadsheet.action';
import {FileManagementService} from '@core/services/file-management.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';

@Component({
    standalone: false,
    selector: 'app-form-view-instructions',
  templateUrl: './form-view-instructions.component.html',
  styleUrls: ['./form-view-instructions.component.scss']
})
export class FormViewInstructionsComponent implements OnInit, OnDestroy, ISideNoteComponent {
  isHelpRequestListVisible = false;
  isInstructionsVisible = false;
  isReleaseNotesVisible = false;
  private selectedCell: any;
  private unsubscribe$ = new Subject<void>();
  reportFormDataItem: any;
  formWarnings: any[];
  mandatoryWarnings: any[];
  mandatoryWarningsText: string;
  advisoryWarnings: any[];
  advisoryWarningsText: string;
  analyticalWarnings: any[];
  analyticalWarningsText: string;
  cellTitle: any;
  cbcForms: any[];
  hasCbcFormsQueryInvoked = {};
  isCbcFormsListVisible: boolean;
  //
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  constructor(private spreadsheetStore: Store<SpreadsheetState>,
              private flexSheetService: FlexSheetService,
              private platformQueryService: PlatformQueryService,
              private fileManagementService: FileManagementService,
              private fuseSplashScreenService: FuseSplashScreenService) { }

  ngOnInit(): void {
    this.flexSheetService.spreadSheetEvent$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: SpreadSheetEventModel) => {
      if (event.name === SpreadSheetEvent.CellSelected) {
        if (!event.payload) {
          return;
        }
        this.selectedCell = event.payload;
        const workbookName = this.flexSheetService.getCurrentWorkBook().fileName;
        const worksheetName = this.flexSheetService.getCurrentWorkSheet().name;
        const cellAddress = this.getExcelCellNameFromZeroBasedIndex(this.selectedCell.ec, this.selectedCell.er);
        this.fetchData(cellAddress, workbookName, worksheetName);
        this.fetchCbcForms();
      }
    });
  }
  toggleHelpRequestList() {
    this.isHelpRequestListVisible = !this.isHelpRequestListVisible;
  }
  toggleInstructionVisible(){
    this.isInstructionsVisible = !this.isInstructionsVisible;
  }
  toggleReleaseNotes() {
    this.isReleaseNotesVisible = !this.isReleaseNotesVisible;
  }
  private getExcelCellNameFromZeroBasedIndex(ec, er) {
    return `$${this.getColumnName(ec + 1)}$${er + 1}`;
  }
  private getColumnName(oneBasedColumnIndex) {
    let dividend = oneBasedColumnIndex;
    let columnName = '';
    while (dividend > 0) {
      const modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = (dividend - modulo) / 26;
      dividend = Math.round(dividend);
    }
    return columnName;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private fetchData(cellAddress: string, workbook: string, worksheet: string) {
    const formViewInstructionQuery = new PlatformQuery();
    formViewInstructionQuery.name = 'GetFormViewInstructionAndWarningsQuery';
    formViewInstructionQuery.api = Api.Spreadsheet;
    formViewInstructionQuery.fieldValues['FormViewId'] = this.magicLinkModel.fieldValues['FormViewId'];
    formViewInstructionQuery.fieldValues['ExcelAddress'] = cellAddress;
    formViewInstructionQuery.fieldValues['Workbook'] = workbook;
    formViewInstructionQuery.fieldValues['Worksheet'] = worksheet;

      this.platformQueryService.executeQuery(formViewInstructionQuery).subscribe(response => {
      this.reportFormDataItem = response.fieldValues['ReportFormDataItem'];
      this.formWarnings = response.fieldValues['FormWarnings'];
      this.cellTitle = response.fieldValues['fieldName'];

      if (this.formWarnings && this.formWarnings.length) {
        this.processFormWarnings(this.formWarnings);
      }
    });
  }

  private processFormWarnings(formWarnings: any[]) {
    this.mandatoryWarnings = formWarnings.filter( w => w.warningTypeText === 'Mandatory');
    this.advisoryWarnings = formWarnings.filter( w => w.warningTypeText === 'Advisory');
    this.analyticalWarnings = formWarnings.filter( w => w.warningTypeText === 'Analytic');

    if (this.mandatoryWarnings && this.mandatoryWarnings.length) {
      this.mandatoryWarningsText = this.mandatoryWarnings.map(w => w.description).join('<br>');
    }

    if (this.advisoryWarnings && this.advisoryWarnings.length) {
      this.advisoryWarningsText = this.advisoryWarnings.map(w => w.description).join('<br>');
    }

    if (this.analyticalWarnings && this.analyticalWarnings.length) {
      this.analyticalWarningsText = this.analyticalWarnings.map(w => w.description).join('<br>');
    }
  }

  private fetchCbcForms() {
    const countryCode = this.magicLinkModel.fieldValues['ReportingCountryCode'];
    if (countryCode) {
      this.spreadsheetStore.pipe( select(getCbcFormsSelector, countryCode), takeUntil(this.unsubscribe$) )
        .subscribe(cbcForms => {
          if (!cbcForms && !this.hasCbcFormsQueryInvoked[countryCode]) {
            this.spreadsheetStore.dispatch(new GetCbcFormsAction(countryCode));
            this.hasCbcFormsQueryInvoked[countryCode] = true;
          } else {
            this.cbcForms = cbcForms;
          }
        });
    }
  }

  toggleIsCbcFormsListVisible() {
    this.isCbcFormsListVisible = !this.isCbcFormsListVisible;
  }

  downloadCbcForm(form: any) {
    this.fuseSplashScreenService.show();
    this.fileManagementService.downloadFileByRelativePath(form.link).pipe(take(1))
      .subscribe(data => {
        const fileName = form.link.split('\\').pop().split('/').pop();
        this.fileManagementService.openSaveFileWindow(data, fileName);
        this.fuseSplashScreenService.hide();
      });
  }
}
